"use client";

import type { LucideIcon } from "lucide-react";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ForwardedRef,
  KeyboardEventHandler,
  PropsWithChildren,
  ReactNode,
} from "react";
import { createContext, forwardRef, useCallback, useContext, useEffect, useRef } from "react";
import { createStore, useStore } from "zustand";
import { CommandMenu } from "../../components/CommandMenu/CommandMenu";
import { Popover } from "../../components/Popover/Popover";
import { cn } from "../../utils";
import { inputTypeClass } from "../variants/input-types";

type SelectOption = object | symbol | string | number | null;

interface SelectState<T extends SelectOption> {
  // Values and functions bound to external state
  open: boolean;
  onOpenChange: (open: boolean) => unknown;
  value: T | null;
  onValueChange: (value: T | null) => unknown;
  searchValue: string | undefined;
  onSearchValueChange: (searchValue: string | undefined) => unknown;

  // Functions called by the components
  handleOpenChange: (open: boolean) => unknown;
  handleValueChange: (value: T | null) => unknown;
  handleSearchValueChange: (searchValue: string | undefined) => unknown;
}

function createSelectStore<T extends SelectOption>(
  initialProps?: Partial<
    Omit<SelectState<T>, "handleOpenChange" | "handleValueChange" | "handleSearchValueChange">
  >
) {
  return createStore<SelectState<T>>()((set, get) => ({
    open: initialProps?.open ?? false,
    onOpenChange: initialProps?.onOpenChange ?? (() => null),
    value: initialProps?.value ?? null,
    onValueChange: initialProps?.onValueChange ?? (() => null),
    searchValue: initialProps?.searchValue,
    onSearchValueChange: initialProps?.onSearchValueChange ?? (() => null),

    handleOpenChange: (open: boolean) => {
      get().onOpenChange?.(open);
      if (!open) {
        set({ open: false, searchValue: undefined });
      } else {
        set({ open });
      }
    },
    handleValueChange: (value: T | null) => {
      get().onValueChange?.(value);
      set({ value, open: false, searchValue: undefined });
    },
    handleSearchValueChange: (searchValue: string | undefined) => {
      get().onSearchValueChange?.(searchValue);
      set({ searchValue });
    },
  }));
}
type SelectStore<T extends SelectOption> = ReturnType<typeof createSelectStore<T>>;

const useIsOpen = <T extends SelectOption>(store: SelectStore<T>) =>
  useStore(store, (state) => state.open);

const useHandleOpenChange = <T extends SelectOption>(store: SelectStore<T>) =>
  useStore(store, (state) => state.handleOpenChange);

const useValue = <T extends SelectOption>(store: SelectStore<T>) =>
  useStore(store, (state) => state.value);

const useHandleValueChange = <T extends SelectOption>(store: SelectStore<T>) =>
  useStore(store, (state) => state.handleValueChange);

const useSearchValue = <T extends SelectOption>(store: SelectStore<T>) =>
  useStore(store, (state) => state.searchValue);

const useHandleSearchValueChange = <T extends SelectOption>(store: SelectStore<T>) =>
  useStore(store, (state) => state.handleSearchValueChange);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SelectContext = createContext<SelectStore<any> | null>(null);

const useSelectStore = <T extends SelectOption>() => {
  const store = useContext(SelectContext) as SelectStore<T>;
  if (!store) throw new Error("useSelectStore must be used within a SelectRoot");
  return store;
};

const SelectTrigger = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<"button"> & {
    error?: boolean;
    readOnly?: boolean;
  }
>(
  (
    { disabled, readOnly, "aria-invalid": ariaInvalid, error, className, children, ...props },
    ref
  ) => {
    const store = useSelectStore();
    const open = useIsOpen(store);
    const handleOpenChange = useHandleOpenChange(store);
    const handleSearchValueChange = useHandleSearchValueChange(store);

    const handleKeyDown: KeyboardEventHandler<HTMLButtonElement> = useCallback(
      (event) => {
        if (open || !!disabled || !!readOnly) return;
        if (event.metaKey || event.ctrlKey || event.altKey) return;
        if (!/^[a-zA-Z0-9]$/.test(event.key)) return; // Only proceed for alphanumeric keys
        handleOpenChange(true);
        handleSearchValueChange(event.key);
      },
      [handleOpenChange, open, disabled, readOnly, handleSearchValueChange]
    );

    return (
      <Popover.Trigger asChild>
        <button
          ref={ref}
          data-slot="select-trigger"
          role="combobox"
          aria-expanded={open}
          aria-invalid={!!ariaInvalid || !!error}
          className={cn(
            inputTypeClass("text"),
            `inline-grid min-w-16 grid-flow-col place-content-center items-center justify-between
            gap-[0.25em] pr-[0.25em] [&_svg:not([class*='size-'])]:my-[0.25em]
            [&_svg:not([class*='size-'])]:size-[1em] [&_svg:not([class*='text-'])]:text-primary/50
            [&_svg]:pointer-events-none`,
            readOnly && "cursor-text select-text disabled:pointer-events-auto disabled:opacity-100",
            open && "border-brand ring-[0.125em] ring-brand/50",
            className
          )}
          onKeyDownCapture={handleKeyDown}
          disabled={!!disabled || !!readOnly}
          {...props}
        >
          {children}
          {!disabled && (
            <ChevronDownIcon className={cn("size-5 transition-transform", open && "rotate-180")} />
          )}
        </button>
      </Popover.Trigger>
    );
  }
);
SelectTrigger.displayName = "SelectTrigger";

function SelectValue<T extends SelectOption>({
  placeholder,
  renderValue,
  className,
  ...props
}: Omit<ComponentPropsWithoutRef<"div">, "children"> & {
  placeholder?: string;
  renderValue: (value: T) => ReactNode;
}) {
  const store = useSelectStore<T>();
  const value = useValue(store);
  return (
    <div
      data-slot="select-value"
      className={cn("line-clamp-1 print:overflow-visible", className)}
      {...props}
    >
      {value !== null && value !== undefined ? (
        renderValue(value)
      ) : (
        <span className="text-primary/50 print:hidden">{placeholder}</span>
      )}
    </div>
  );
}

function SelectContent({
  searchPlaceholder = "Search by name...",
  align = "start",
  className,
  children,
  enableSearch = false,
  ...props
}: ComponentPropsWithoutRef<typeof Popover.Content> & {
  searchPlaceholder?: string;
  enableSearch?: boolean;
}) {
  const store = useSelectStore();
  const searchValue = useSearchValue(store);
  const handleSearchValueChange = useHandleSearchValueChange(store);

  const searchRef = useRef<HTMLInputElement>(null);
  const focusFixTimeout = useRef<number | null>(null);
  useEffect(() => {
    if (!window?.setTimeout) return;
    if (focusFixTimeout.current) window.clearTimeout(focusFixTimeout.current);
    // The CommandMenu component automatically selects all text when the search input gains focus.
    // We need to override this behavior because when users start typing to open the popover,
    // we want to preserve their first typed character instead of selecting/replacing it.
    focusFixTimeout.current = window.setTimeout(() => {
      if (!searchRef.current) return;
      searchRef.current.focus();
      searchRef.current.selectionStart = searchRef.current.value.length ?? 0;
    }, 10);
  }, [searchValue]);

  return (
    <Popover.Content align={align} className={cn("p-0 print:hidden", className)} {...props}>
      <CommandMenu>
        {enableSearch && (
          <CommandMenu.Search
            ref={searchRef}
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={handleSearchValueChange}
          />
        )}
        <CommandMenu.List>{children}</CommandMenu.List>
      </CommandMenu>
    </Popover.Content>
  );
}

function SelectGroup({ ...props }: ComponentPropsWithRef<typeof CommandMenu.Group>) {
  return <CommandMenu.Group {...props} />;
}

function SelectSeparator({ ...props }: ComponentPropsWithRef<typeof CommandMenu.Separator>) {
  return <CommandMenu.Separator {...props} />;
}

function SelectItem<T extends SelectOption>({
  value,
  searchValue,
  onSelect,
  children,
  ...props
}: Omit<ComponentPropsWithRef<typeof CommandMenu.Item>, "value" | "onSelect"> & {
  value: T;
  searchValue: string;
  onSelect?: (value: T) => unknown;
}) {
  const store = useSelectStore<T>();
  const selectedValue = useValue(store);
  const handleValueChange = useHandleValueChange(store);

  const isSelected = selectedValue !== null && value === selectedValue;
  return (
    <CommandMenu.Item
      value={searchValue}
      onSelect={() => {
        onSelect?.(value);
        handleValueChange(value);
      }}
      {...props}
    >
      <CheckIcon className={cn("size-4 shrink-0 opacity-0", isSelected && "opacity-100")} />
      {children}
    </CommandMenu.Item>
  );
}

function SelectRootContent<T extends SelectOption>(
  props: Omit<ComponentPropsWithRef<typeof Popover>, "open" | "onOpenChange">
) {
  const store = useSelectStore<T>();
  const open = useIsOpen(store);
  const handleOpenChange = useHandleOpenChange(store);
  return <Popover data-slot="select" open={open} onOpenChange={handleOpenChange} {...props} />;
}

function SelectRoot<T extends SelectOption>({
  open = false,
  onOpenChange,
  value,
  onValueChange,
  onSearchValueChange,
  ...props
}: ComponentPropsWithRef<typeof Popover> & {
  value?: T | null;
  onValueChange?: (value: T | null) => unknown;
  onSearchValueChange?: (searchValue: string | undefined) => unknown;
}) {
  const store = useRef(
    createSelectStore({
      open,
      onOpenChange,
      value,
      onValueChange,
      onSearchValueChange,
    })
  ).current;

  useEffect(() => {
    // Sync store with external state when it changes
    store.setState((prev) => ({
      ...prev,
      open: open ?? prev.open,
      onOpenChange: onOpenChange ?? prev.onOpenChange,
      value: value ?? prev.value,
      onValueChange: onValueChange ?? prev.onValueChange,
      onSearchValueChange: onSearchValueChange ?? prev.onSearchValueChange,
    }));
  }, [open, onOpenChange, value, onValueChange, onSearchValueChange, store]);

  return (
    <SelectContext.Provider value={store}>
      <SelectRootContent {...props} />
    </SelectContext.Provider>
  );
}

type SelectAbstractProps<T extends SelectOption> = {
  /**
   * Whether the popover is open.
   * @default false
   */
  open?: boolean;
  /**
   * Callback function that is called when the popover is opened or closed.
   */
  onOpenChange?: (open: boolean) => unknown;
  /**
   * The value of the selected item.
   */
  value?: T | null;
  /**
   * Callback function that is called when the value of the selected item changes.
   */
  onValueChange?: (value: T | null) => unknown;
  /**
   * The function that renders a value for display to the user.
   */
  renderValue: (value: T) => ReactNode;
  /**
   * Whether the search input is enabled.
   */
  enableSearch?: boolean;
  /**
   * Callback function that is called when the search value changes.
   */
  onSearchValueChange?: (searchValue: string | undefined) => unknown;
  /**
   * The placeholder text to display when no value is selected.
   */
  placeholder?: string;
  /**
   * The content to display when there are no items to show.
   */
  emptyContent?: ReactNode;
  /**
   * Whether the select is loading.
   */
  loading?: boolean;
  /**
   * The content to display when the select is loading.
   */
  loadingContent?: ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  leftIcon?: LucideIcon;
  className?: string;
  children: ReactNode;
};

function SelectAbstract<T extends SelectOption>(
  {
    open,
    onOpenChange,
    value,
    onValueChange,
    renderValue,
    enableSearch,
    onSearchValueChange,
    placeholder,
    emptyContent = "No items to show",
    loading,
    loadingContent = "Loading...",
    disabled,
    readOnly,
    error,
    leftIcon: LeftIcon,
    className,
    children,
  }: SelectAbstractProps<T>,
  ref?: ForwardedRef<HTMLButtonElement>
) {
  return (
    <SelectRoot
      open={open}
      onOpenChange={onOpenChange}
      value={value}
      onValueChange={onValueChange}
      onSearchValueChange={onSearchValueChange}
    >
      <SelectTrigger
        ref={ref}
        disabled={disabled}
        readOnly={readOnly}
        error={error}
        className={className}
      >
        {LeftIcon && <LeftIcon />}
        <SelectValue placeholder={placeholder} renderValue={renderValue} />
      </SelectTrigger>
      <SelectContent enableSearch={enableSearch}>
        {loading ? (
          <CommandMenu.Loading>{loadingContent}</CommandMenu.Loading>
        ) : (
          <>
            <CommandMenu.Empty>{emptyContent}</CommandMenu.Empty>
            {children}
          </>
        )}
      </SelectContent>
    </SelectRoot>
  );
}

const Select = forwardRef(SelectAbstract) as <T extends SelectOption>(
  props: PropsWithChildren<SelectAbstractProps<T>> & {
    ref?: ForwardedRef<HTMLButtonElement>;
  }
) => ReturnType<typeof SelectAbstract>;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectRoot,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
